import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

export default function Register() {
  const [formData, setFormData] = useState({
    school_name: '',
    email: '',
    phone: '',
    address: '',
    name: '',
    username: '',
    password: '',
    confirmPassword: '',
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const { register, isAuthenticated, error, clearError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    clearError();
  }, [clearError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'password' || name === 'confirmPassword') setPasswordError('');
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }
    if (formData.password.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    const { confirmPassword, ...submitData } = formData;
    const result = await register(submitData);
    if (result.success) navigate('/dashboard', { replace: true });
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 py-12">
      <div className="max-w-5xl w-full bg-white shadow-lg rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2">
        {/* Left Side - School Details */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white p-10 flex flex-col justify-center space-y-6">
          <h2 className="text-3xl font-bold">School Registration</h2>
          <div className="mt-6">
            <label className="text-sm font-medium">School Logo</label>
            <div className="flex items-center space-x-4 mt-2">
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="text-sm text-blue-100 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-700 file:text-white hover:file:bg-blue-800"
              />
              {logoPreview && (
                <img src={logoPreview} alt="School Logo Preview" className="w-12 h-12 object-cover rounded-md border" />
              )}
            </div>
          </div>

          <div className="space-y-4 mt-6">
            <div>
              <label className="text-sm font-medium">School Name *</label>
              <input type="text" name="school_name" value={formData.school_name} onChange={handleChange} placeholder="Enter school name" required className="mt-1 w-full px-4 py-2 rounded-md text-gray-900" />
            </div>

            <div>
              <label className="text-sm font-medium">Email *</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter school email" required className="mt-1 w-full px-4 py-2 rounded-md text-gray-900" />
            </div>

            <div>
              <label className="text-sm font-medium">Phone *</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="Enter phone number" required className="mt-1 w-full px-4 py-2 rounded-md text-gray-900" />
            </div>

            <div>
              <label className="text-sm font-medium">Address *</label>
              <textarea name="address" rows={3} value={formData.address} onChange={handleChange} placeholder="Enter school address" required className="mt-1 w-full px-4 py-2 rounded-md text-gray-900"></textarea>
            </div>
          </div>
        </div>

        {/* Right Side - User/Admin Details */}
        <div className="p-10 bg-white flex flex-col justify-center space-y-6">
          <h2 className="text-2xl font-bold text-gray-800 text-center">Admin Account</h2>

          {(error || passwordError) && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-md text-sm">
              {error || passwordError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Full Name *</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Enter full name" required className="mt-1 w-full px-3 py-2 border rounded-md" />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Username *</label>
              <input type="text" name="username" value={formData.username} onChange={handleChange} placeholder="Enter username" required className="mt-1 w-full px-3 py-2 border rounded-md" />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Password *</label>
              <div className="relative mt-1">
                <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} placeholder="Enter password" required className="w-full px-3 py-2 border rounded-md pr-10" />
                <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Confirm Password *</label>
              <div className="relative mt-1">
                <input type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword}
                  onChange={handleChange} placeholder="Confirm your password" required className="w-full px-3 py-2 border rounded-md pr-10" />
                <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <button type="submit" disabled={isSubmitting} className="w-full mt-4 bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition disabled:opacity-50">
              {isSubmitting ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-600">
            Already have an account?{' '} <Link to="/login" className="text-blue-600 hover:underline"> Sign in </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
